import { useQuery } from '@tanstack/react-query'
import { ReadOnlyBanner } from '@yihang/ui/primitives'
import { TenantShell, type ShellNavItem } from '@yihang/ui/app-shell'
import Spin from 'antd/es/spin'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { queryKeys, tenantApi } from '../api/client'
import { useAppState } from '../state/app-state'
import { useExtensionCollectionEvents } from '../state/use-extension-collection-events'
import { useTenantEvents } from '../state/use-tenant-events'
import { productCollectionStates, productCollectionState, productCollectionUrl } from '../domain/product-collection-navigation'

const commonNavItems: readonly ShellNavItem[] = [
  { key: 'dashboard', label: '工作台', icon: 'grid' },
  { key: 'data-screen', label: '大屏数据', icon: 'grid' },
  { key: 'plugin-download', label: '插件下载', icon: 'download' },
  { key: 'shops', label: '店铺授权', icon: 'store' },
  { key: 'products', label: 'Ozon 采集库', icon: 'database', children:
    productCollectionStates.map(item => ({ key: `products-${item.key}`, label: item.label, icon: 'database' })) },
  { key: 'wb', label: 'WB 采集库', icon: 'database' },
  { key: 'image-to-psd', label: '图片转 PSD', icon: 'sparkles' },
  { key: 'image-rewrites', label: '独立图片二创', icon: 'sparkles' },
  { key: 'store-performance', label: '店铺商品表现', icon: 'grid' },
  { key: 'published', label: '上架商品库', icon: 'upload' },
  {
    key: 'calculators',
    label: '计算器',
    icon: 'calculator',
    children: [
      { key: 'ozon-calculator', label: 'Ozon 计算器', icon: 'calculator' },
      { key: 'pricing-calculator', label: '定价计算器', icon: 'calculator' },
      { key: 'profit-calculator', label: '利润计算器', icon: 'calculator' },
    ],
  },
  { key: 'orders', label: '订单列表', icon: 'orders' },
  { key: 'ai-rewrite-logs', label: '二创日志', icon: 'tasks' },
  { key: 'messages', label: '消息中心', icon: 'logs' },
]

const adminNavItems: readonly ShellNavItem[] = [
  ...commonNavItems,
  { key: 'accounts', label: '子账号管理', icon: 'user' },
  { key: 'point-ledger', label: '积分明细', icon: 'logs' },
  { key: 'settings', label: '系统设置', icon: 'settings' },
]

const pathByKey: Record<string, string> = {
  ...Object.fromEntries(
    adminNavItems.filter((item) => !item.children).map((item) => [item.key, `/${item.key}`]),
  ),
  ...Object.fromEntries(
    adminNavItems.flatMap((item) => item.children ?? []).map((item) => [item.key, `/${item.key}`]),
  ),
  'selection-rules': '/auto-selection/rules',
  'selection-products': '/auto-selection/products',
}

function resolveActiveKey(pathname: string, navItems: readonly ShellNavItem[], search = '') {
  if (pathname.startsWith('/products')) return `products-${productCollectionState(search)}`
  if (pathname.startsWith('/auto-selection/products')) return 'selection-products'
  if (pathname.startsWith('/auto-selection')) return 'selection-rules'
  if (
    pathname.startsWith('/published') ||
    pathname.startsWith('/validation') ||
    pathname.startsWith('/publish-result')
  )
    return 'published'
  if (pathname.startsWith('/orders')) return 'orders'
  if (pathname.startsWith('/ai-rewrite-logs')) return 'ai-rewrite-logs'
  if (pathname.startsWith('/messages')) return 'messages'
  const activeChild = navItems
    .flatMap((item) => item.children ?? [])
    .find((item) => {
      const itemPath = pathByKey[item.key]
      return itemPath ? pathname.startsWith(itemPath) : false
    })
  if (activeChild) return activeChild.key
  return navItems.find((item) => pathname.startsWith(`/${item.key}`))?.key ?? 'dashboard'
}

export function ProtectedLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { authenticated, bootstrapping, clearSession, readOnly, session } = useAppState()
  const standaloneDataScreen = location.pathname === '/data-screen'
  useTenantEvents(authenticated && !bootstrapping && !standaloneDataScreen)
  useExtensionCollectionEvents(authenticated && !bootstrapping && !standaloneDataScreen)
  const notificationsQuery = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: async () => (await tenantApi.notifications()).data,
    enabled: authenticated && !standaloneDataScreen,
    refetchInterval: 30_000,
  })
  const pointBalanceQuery = useQuery({
    queryKey: queryKeys.aiPointBalance,
    queryFn: async () => (await tenantApi.aiPointBalance()).data,
    enabled: authenticated && !standaloneDataScreen,
    refetchInterval: 15_000,
  })

  if (bootstrapping) {
    return (
      <div className="route-fallback" role="status" aria-live="polite">
        <Spin />
        <span>正在恢复安全会话…</span>
      </div>
    )
  }
  if (!authenticated)
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    )

  if (standaloneDataScreen)
    return (
      <main className="data-screen-standalone">
        <Outlet />
      </main>
    )

  const isMainAccount = session?.user.role === 'TENANT_ADMIN'
  const navItems: readonly ShellNavItem[] = isMainAccount ? adminNavItems : [...commonNavItems,
    { key: 'settings', label: '我的图片水印', icon: 'settings' }]
  const activeKey = resolveActiveKey(location.pathname, navItems, location.search)
  const unreadCount = notificationsQuery.data?.items.filter((item) => !item.readAt).length ?? 0
  const licenseLabel = `剩余积分 ${pointBalanceQuery.data?.balance ?? '--'}`

  const logout = async () => {
    await tenantApi.logout().catch(() => undefined)
    clearSession()
    navigate('/login', { replace: true })
  }

  return (
    <TenantShell
      activeKey={activeKey}
      licenseLabel={licenseLabel}
      logoSrc="/brand/yihang-logo.png"
      navItems={navItems}
      notificationCount={unreadCount}
      readOnly={readOnly}
      userName={session?.user.email ?? '账号信息未加载'}
      onLicenseClick={isMainAccount ? () => navigate('/point-ledger') : undefined}
      onNavigate={(key) => {
        if (key.startsWith('products-')) {
          navigate(productCollectionUrl(key.slice('products-'.length), location.pathname === '/products' ? location.search : ''))
          return
        }
        const path = pathByKey[key] ?? '/dashboard'
        if (key === 'data-screen') {
          window.open(path, '_blank', 'noopener,noreferrer')
          return
        }
        navigate(path)
      }}
      onNotifications={() => navigate('/notifications')}
      onUser={isMainAccount ? () => navigate('/settings?tab=device') : undefined}
      onLogout={() => void logout()}
    >
      {readOnly ? (
        <div className="page-stack">
          <ReadOnlyBanner
            onDetails={() => navigate(isMainAccount ? '/point-ledger' : '/dashboard')}
          />
          <Outlet />
        </div>
      ) : (
        <Outlet />
      )}
    </TenantShell>
  )
}
